import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@web/components/ui/tabs';

import { ContactData } from './contact-data';
import { ContactOverview } from './contact-overview';
import { ConversationAnalysis } from './conversation-analysis';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarProvider,
  useSidebar,
} from '@web/components/ui/sidebar';

import { Button } from '@web/components/ui/button';
import { useIsMobile } from '@web/hooks';
import { ScrollArea } from '@web/components/ui/scroll-area';
import { X, User2, ChevronLeft } from 'lucide-react';
import { Route } from '@web/routes/dashboard/route';

export function ContactInfoSidebar({
  active,
  contactId,
}: {
  active: boolean;
  contactId: string | null;
}) {
  const isMobile = useIsMobile();
  const { setContactInfoSidebarContactId } = Route.useRouteContext();

  return (
    <SidebarProvider
      name="right"
      className="w-min"
      open={active && contactId !== null}
      style={{
        '--sidebar-width': isMobile ? '100vw' : '400px',
      }}
    >
      <Sidebar
        variant="floating"
        collapsible="offcanvas"
        side="right"
        className={`${isMobile ? 'w-full' : ''} shadow-lg`}
      >
        <SidebarHeader className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-4 py-3">
          <div className="flex items-center">
            {isMobile && (
              <Button
                variant="ghost"
                size="sm"
                className="mr-2 h-9 w-9 rounded-md p-0"
                onClick={() => {
                  setContactInfoSidebarContactId(null);
                }}
              >
                <ChevronLeft className="h-5 w-5" />
                <span className="sr-only">Back</span>
              </Button>
            )}
            <h2 className="text-base font-medium">Contact Details</h2>
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="h-9 w-9 rounded-md p-0"
            onClick={() => {
              setContactInfoSidebarContactId(null);
            }}
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </Button>
        </SidebarHeader>

        {contactId !== null && (
          <SidebarContent className="flex h-[calc(100vh-3.5rem)] flex-col overflow-hidden">
            <ScrollArea className="h-full" viewportClassName="h-full">
              <div className="border-b px-4 py-4">
                <SidebarGroup className="mb-2 flex flex-col" key={contactId}>
                  <ContactOverview contactId={contactId} />
                </SidebarGroup>
              </div>

              <Tabs
                defaultValue="contact"
                className="flex w-full flex-col text-left"
              >
                <div className="sticky top-0 z-10 bg-white">
                  <TabsList className="grid w-full grid-cols-2 bg-transparent p-0">
                    <TabsTrigger
                      value="contact"
                      className="data-[state=active]:text-primary data-[state=active]:after:bg-primary rounded-none border-0 py-3 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-transparent after:content-[''] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                    >
                      Contact Details
                    </TabsTrigger>
                    <TabsTrigger
                      value="AI"
                      className="data-[state=active]:text-primary data-[state=active]:after:bg-primary rounded-none border-0 py-3 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-transparent after:content-[''] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                    >
                      AI Insights
                    </TabsTrigger>
                  </TabsList>
                </div>

                <div className="p-0">
                  <TabsContent value="contact" className="m-0 border-0 p-0">
                    <div className="p-4">
                      <ContactData contactId={contactId} />
                    </div>
                  </TabsContent>

                  <TabsContent value="AI" className="m-0 border-0 p-0">
                    <div className="p-4">
                      <ConversationAnalysis
                        key={contactId}
                        contactId={contactId}
                      />
                    </div>
                  </TabsContent>
                </div>
              </Tabs>
            </ScrollArea>
          </SidebarContent>
        )}
      </Sidebar>
    </SidebarProvider>
  );
}
